/**
 * Two-field dashboard "last here" timestamps (per Clerk user).
 *
 * - recent: this login/session's stamp
 * - previous: the login before that (what we show after the first session)
 *
 * Rotation happens only when Clerk sessionId changes — not on every remount.
 */

const STORAGE_PREFIX = "ls:dashboard:loginVisit:";
const LEGACY_PREFIX = "ls:dashboard:lastVisit:";

function storageKey(userId) {
  return `${STORAGE_PREFIX}${String(userId || "").trim()}`;
}

function legacyKey(userId) {
  return `${LEGACY_PREFIX}${String(userId || "").trim()}`;
}

function readJson(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota / private mode
  }
}

function isValidIso(value) {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}

/**
 * Load stored state, migrating the old single-timestamp key if present.
 * @returns {{ previous: string|null, recent: string|null, sessionId: string|null }}
 */
function loadState(userId) {
  const id = String(userId || "").trim();
  if (!id || typeof window === "undefined") {
    return { previous: null, recent: null, sessionId: null };
  }

  const stored = readJson(storageKey(id));
  if (stored && typeof stored === "object") {
    return {
      previous: isValidIso(stored.previous) ? stored.previous : null,
      recent: isValidIso(stored.recent) ? stored.recent : null,
      sessionId: stored.sessionId ? String(stored.sessionId) : null,
    };
  }

  // Migrate legacy single stamp → treat as previous (never as current display overwrite)
  try {
    const legacy = window.localStorage.getItem(legacyKey(id));
    if (isValidIso(legacy)) {
      const migrated = { previous: legacy, recent: null, sessionId: null };
      writeJson(storageKey(id), migrated);
      try {
        window.localStorage.removeItem(legacyKey(id));
      } catch {
        // ignore
      }
      return migrated;
    }
  } catch {
    // ignore
  }

  return { previous: null, recent: null, sessionId: null };
}

/**
 * Ensure login timestamps for this Clerk session, then return what the UI should show.
 *
 * - First login ever (no previous): { labelSource: "just_now" }
 * - Later login: { labelSource: "previous", iso }
 * - Same session remount: no rotation; same display rules
 *
 * @param {string|null|undefined} userId
 * @param {string|null|undefined} sessionId - Clerk session id
 * @returns {{ labelSource: "just_now" } | { labelSource: "previous", iso: string } | null}
 */
export function resolveDashboardLastHere(userId, sessionId) {
  const id = String(userId || "").trim();
  const sid = String(sessionId || "").trim();
  if (!id || !sid || typeof window === "undefined") return null;

  const key = storageKey(id);
  let state = loadState(id);
  const nowIso = new Date().toISOString();

  if (state.sessionId !== sid) {
    // New login/session: shift recent → previous, stamp recent
    const next = {
      previous: state.recent || state.previous || null,
      recent: nowIso,
      sessionId: sid,
    };
    writeJson(key, next);
    state = next;
  }

  if (state.previous && isValidIso(state.previous)) {
    return { labelSource: "previous", iso: state.previous };
  }

  // First login (or migrated with no previous yet while this is the first stamped session)
  return { labelSource: "just_now" };
}
